import { useChat } from "@ai-sdk/react"
import { useEffect, useCallback } from "react"
import { useLLM } from "@/contexts/llm-context"
import { useChatMessages } from "@/contexts/chat/ChatMessagesContext"
import { useChatInput } from "@/contexts/chat/ChatInputContext"
import { useChatLoading } from "@/contexts/chat/ChatLoadingContext"
import { useDocset } from "@/contexts/chat/DocsetContext"
import { ChatApiService } from "@/services/ChatApiService"
import { ChatBusinessService } from "@/services/ChatBusinessService"

export function useChatIntegration(selectedDocset?: string) {
  const { selectedProvider } = useLLM()
  const { messages, setMessages, removeMessage, clearMessages } = useChatMessages()
  const { input, setInput } = useChatInput()
  const { 
    setStatus, 
    setLoading, 
    setStreaming, 
    setError, 
    setManuallyStopped, 
    stopManually, 
    reset,
    isManuallyStopped,
    status
  } = useChatLoading()
  const docset = useDocset()

  // Eğer selectedDocset parametresi verilmişse onu kullan, yoksa context'teki docset'i kullan
  const currentDocset = selectedDocset || docset.selectedDocset



  const chatConfig = ChatApiService.getChatConfig(selectedProvider, currentDocset)

  const {
    messages: aiMessages,
    handleSubmit: aiHandleSubmit,
    append: aiAppend,
    stop: aiStop,
    status: aiStatus,
    error: aiError,
    reload: aiReload,
  } = useChat({
    ...chatConfig,
    onResponse: (response) => {
      ChatBusinessService.processResponse(response)
      setManuallyStopped(false)
    },
    onFinish: (message) => {
      ChatBusinessService.processFinishedMessage(message)
      setManuallyStopped(false)
    },
    onError: (error) => {
      ChatBusinessService.handleError(error)
      setManuallyStopped(false)
      setError(error)
    }
  })

  useEffect(() => {
    setMessages(aiMessages)
  }, [aiMessages, setMessages])

  useEffect(() => {
    setStatus(aiStatus as any)
    const isLoading = ChatBusinessService.isLoadingState(aiStatus as any, isManuallyStopped)
    setLoading(isLoading)
    setStreaming(aiStatus === "streaming")
  }, [aiStatus, setStatus, setLoading, setStreaming, isManuallyStopped])

  useEffect(() => {
    setError(aiError)
  }, [aiError, setError])

  useEffect(() => {
    ChatBusinessService.checkMessageForErrors(aiMessages, (messageId) => {
      removeMessage(messageId)
    })
  }, [aiMessages, removeMessage])

  useEffect(() => {
    if (docset.previousDocset && 
        ChatBusinessService.shouldClearMessagesOnDocsetChange(docset.previousDocset, docset.selectedDocset)) {
      clearMessages()
    }
  }, [docset.selectedDocset, docset.previousDocset, clearMessages])

  const handleSubmit = useCallback((
    event?: { preventDefault?: () => void },
    options?: { experimental_attachments?: FileList }
  ) => {
    if (!ChatBusinessService.validateMessageBeforeSend(input)) {
      return
    }

    event?.preventDefault?.()
    setManuallyStopped(false)
    
    // Mesajı manuel olarak append et
    aiAppend({
      role: "user",
      content: input
    })
    
    // Input'u temizle
    setInput("")
  }, [input, setManuallyStopped, aiAppend, setInput])

  const handleStop = useCallback(() => {
    stopManually()
    aiStop()
  }, [stopManually, aiStop])

  const handleClearMessages = useCallback(() => {
    if (status === "submitted" || status === "streaming") {
      stopManually()
      aiStop()
    }
    clearMessages()
    reset()
  }, [status, stopManually, aiStop, clearMessages, reset])

  return {
    handleSubmit,
    append: aiAppend,
    stop: handleStop,
    reload: aiReload,
    clearMessages: handleClearMessages,
  }
} 